import { createAction, createEntityAdapter, Dictionary, EntityId, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { UnifiedFilterConfig } from '../gzDashboardFilters/types';
import { FiltersHelper } from '../gzDashboardFilters/helpers/filters.helper';
import { DashboardAppState } from './dashboards.app.reducer';
import { DashboardsReduxActions } from '../types/types';
import {
  checkIfHasUnsavedChanges,
  selectActiveViewConfiguration,
  selectDefaultView,
} from './dashboards.smart.views.slice';
import { Filter } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { isEmpty, isEqual } from 'lodash';
import { DashboardSmartView } from '../gzDashboardSmartViewsList/types';
import { AllFilterTypeConditions } from '@nirvana/shared/dtos/types/shared/filters';
import { StringConditions } from '@nirvana/shared/models/nestjs';

export const filtersAdapter = createEntityAdapter<UnifiedFilterConfig>({
  selectId: FiltersHelper.getFilterIdForStore,
  sortComparer: FiltersHelper.filterComparer,
});

export interface FiltersState {
  filters: EntityState<UnifiedFilterConfig>;
}

export const filtersInitialState: FiltersState = {
  filters: filtersAdapter.getInitialState(),
};

// SELECTORS
export const selectDashboardFilterValues = (state: DashboardAppState): Array<Filter> =>
  FiltersHelper.filterValuesFromUnifiedConfigs(
    Object.values(
      filtersAdapter.getSelectors().selectEntities(state.filtersSlice.filters)
    ) as Array<UnifiedFilterConfig>
  );

// We use an extra map here because we want to unset a prop, which is defined by default with configurable:false
export const selectDashboardFilterConfigs = (state: DashboardAppState) =>
  FiltersHelper.baseFilterConfigsFromUnified(
    Object.values(filtersAdapter.getSelectors().selectEntities(state.filtersSlice.filters)).map(config => ({
      ...config,
    })) as Array<UnifiedFilterConfig>
  );

export const filtersSelectors = filtersAdapter.getSelectors((state: DashboardAppState) => state.filtersSlice.filters);

// active filters are the ones that have a value assigned, even an empty text filter has the value: "contains: ''"
export const selectActiveFilters = (state: DashboardAppState): Array<UnifiedFilterConfig> => {
  const allFilters = filtersSelectors.selectAll(state);
  return allFilters.filter(filter => filter.value);
};

// HELPERS
export const setFiltersToState = (state: DashboardAppState, payload: Array<UnifiedFilterConfig>) => {
  filtersAdapter.upsertMany(state.filtersSlice.filters, payload);
  state.viewsSlice.hasUnsavedChanges = checkIfHasUnsavedChanges(state);
};

export const checkIfFiltersChanged = (state: DashboardAppState) => {
  const updatedFilters = selectActiveFilters(state);
  const activeView = selectActiveViewConfiguration(state);
  if (activeView.filters.length !== updatedFilters.length) {
    return true;
  }
  const viewFiltersKeys = activeView.filters.map(filter => Object.keys(filter)[0]);
  const updatedFiltersKeys = updatedFilters.map(filter => filter.key);

  if (!isEqual(viewFiltersKeys.sort(), updatedFiltersKeys.sort())) {
    return true;
  }

  for (const viewFilter of activeView.filters) {
    const updatedFilter = updatedFilters.find(filter => filter.key === Object.keys(viewFilter)[0]);
    if (!updatedFilter) {
      throw new Error('Could not find updated filter');
    }

    // this is used when a certain filter is empty, ex: tags: {} compared with tags: { in: [] }
    if (isEmpty(Object.values(viewFilter)[0]) && isEmpty(Object.values(updatedFilter.value || {})[0])) {
      continue;
    }

    if (!isEqual(Object.values(viewFilter)[0], updatedFilter.value)) {
      return true;
    }
  }

  return false;
};

// ACTIONS
export const setFilters = createAction<Array<UnifiedFilterConfig>>(DashboardsReduxActions.SET_DASHBOARD_FILTERS);
export const setFiltersActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<Array<UnifiedFilterConfig>>
): void => {
  setFiltersToState(state, action.payload);
};

export const resetFilters = createAction(DashboardsReduxActions.RESET_FILTERS);
export const resetFiltersActionHandler = (state: DashboardAppState) => {
  const filtersInRedux = filtersSelectors.selectEntities(state);
  const filtersInReduxIds = filtersSelectors.selectIds(state);

  const filtersToSet: Array<UnifiedFilterConfig> = [];

  let viewConfig: DashboardSmartView;
  if (state.uiSlice.configuration.smartViewsPanel.visible) {
    // dashboard with smart views
    viewConfig = selectActiveViewConfiguration(state);
  } else {
    // dashboard without smart views, so we need to get the default view configuration
    viewConfig = selectDefaultView(state);
  }

  if (!viewConfig) {
    throw new Error('View config not found.');
  }

  // we need to set the saved value on filters saved on view
  for (const filter of viewConfig.filters) {
    const filterConfig = filtersInRedux[Object.keys(filter)[0]];

    if (!filterConfig) {
      continue;
    }
    filtersToSet.push({
      ...filterConfig,
      value: Object.values(filter)[0],
    });
  }

  // we need to set value - undefined on all filters not saved on view
  for (const filterId of filtersInReduxIds) {
    if (viewConfig.filters.filter(filter => Object.keys(filter)[0] === filterId).length === 0) {
      const filterConfig = filtersInRedux[filterId];

      if (!filterConfig) {
        continue;
      }

      filtersToSet.push({
        ...filterConfig,
        value: undefined,
      });
    }
  }

  setFiltersToState(state, filtersToSet);
};
